// Google + Phone sign-in, shared by SignInPage + SignUpPage. For OAuth /
// phone providers "sign in" and "sign up" are the same action (the account
// is created on first use), so both pages render this identically.
//
// On success, useAuth's onAuthStateChanged sets `user` and the host page's
// redirect effect takes over — this component never navigates itself.

import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

// The invisible reCAPTCHA container id. Only one auth page mounts at a time,
// so a single constant id is safe.
const RECAPTCHA_ID = 'recaptcha-auth-providers';

// Jul 14 — phone sign-in is OFF for launch. Email + Google only. Phone-primary
// on a free-trial product invites SMS-pumping toll fraud (you pay per SMS AND
// the abuser farms free credits). Re-enable by flipping this to true AFTER
// wiring Firebase App Check. The full phone flow below stays intact so it's a
// one-line switch. (Also add Firebase test phone numbers before testing.)
const SHOW_PHONE_SIGN_IN = false;

export default function AuthProviders() {
  const { signInWithGoogle, startPhoneSignIn, confirmPhoneCode } = useAuth();
  const [mode, setMode] = useState<'buttons' | 'phone' | 'code'>('buttons');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    const { error: err } = await signInWithGoogle();
    setBusy(false);
    if (err) setError(err);
    // success → onAuthStateChanged → host page redirects
  };

  const handleSendCode = async () => {
    setError('');
    if (!phone.trim()) { setError('Enter your phone number.'); return; }
    setBusy(true);
    const { error: err } = await startPhoneSignIn(phone, RECAPTCHA_ID);
    setBusy(false);
    if (err) setError(err);
    else setMode('code');
  };

  const handleConfirm = async () => {
    setError('');
    if (!code.trim()) { setError('Enter the code we texted you.'); return; }
    setBusy(true);
    const { error: err } = await confirmPhoneCode(code);
    setBusy(false);
    if (err) setError(err);
    // success → onAuthStateChanged → host page redirects
  };

  const inputCls =
    'w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all';
  const secondaryBtn =
    'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface-dark/60 bg-white text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';
  const primaryBtn =
    'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <div className="mt-6">
      {/* Divider */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px flex-1 bg-surface-dark/40" />
        <span className="text-xs text-dark-muted">or continue with</span>
        <div className="h-px flex-1 bg-surface-dark/40" />
      </div>

      {error && <p className="text-red-600 text-xs mb-3 text-center">{error}</p>}

      {mode === 'buttons' && (
        <div className="space-y-3">
          <button type="button" onClick={handleGoogle} disabled={busy} className={secondaryBtn}>
            <GoogleG />
            Continue with Google
          </button>
          {SHOW_PHONE_SIGN_IN && (
            <button
              type="button"
              onClick={() => { setError(''); setMode('phone'); }}
              disabled={busy}
              className={secondaryBtn}
            >
              <i className="ri-smartphone-line text-base" />
              Continue with phone
            </button>
          )}
        </div>
      )}

      {mode === 'phone' && (
        <div className="space-y-3">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            autoFocus
            className={inputCls}
          />
          <button type="button" onClick={handleSendCode} disabled={busy} className={primaryBtn}>
            {busy ? (
              <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <>
                <i className="ri-message-2-line" />
                Send code
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => { setError(''); setMode('buttons'); }}
            className="w-full text-center text-xs text-dark-muted hover:text-dark transition-colors cursor-pointer"
          >
            Back
          </button>
        </div>
      )}

      {mode === 'code' && (
        <div className="space-y-3">
          <p className="text-xs text-dark-muted text-center">
            Enter the code we texted to <span className="font-medium text-dark">{phone}</span>
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            autoFocus
            className={`${inputCls} text-center tracking-[0.4em] font-semibold`}
          />
          <button type="button" onClick={handleConfirm} disabled={busy} className={primaryBtn}>
            {busy ? (
              <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <>
                <i className="ri-check-line" />
                Verify &amp; continue
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => { setError(''); setCode(''); setMode('phone'); }}
            className="w-full text-center text-xs text-dark-muted hover:text-dark transition-colors cursor-pointer"
          >
            Use a different number
          </button>
        </div>
      )}

      {/* Invisible reCAPTCHA — must exist in the DOM before startPhoneSignIn. */}
      {SHOW_PHONE_SIGN_IN && <div id={RECAPTCHA_ID} />}
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.42 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}
